// Copyright 2026 Monterosa Productions Limited. All rights reserved.

import { saveConsent, type ConsentPrefs } from './consent.js';

declare global {
  interface Window {
    OnetrustActiveGroups?: string;
    OptanonWrapper?: () => void;
    __proximaConsentCallback?: () => void;
    OneTrust?: {
      ToggleInfoDisplay?: () => void;
      // Fires whenever the user saves a consent change. The event detail is the
      // array of active group IDs (e.g. ["C0001","C0002"]) at the moment of the
      // change — the authoritative, freshly-updated value.
      OnConsentChanged?: (cb: (event: Event) => void) => void;
    };
  }
}

// Map a list of active OneTrust group IDs to our consent preferences.
function mapActiveGroups(activeGroupIds: string[]): ConsentPrefs {
  return {
    analytics: activeGroupIds.includes('C0002'),
    marketing: activeGroupIds.includes('C0004'),
  };
}

// Read each group's explicit on/off state from the OptanonConsent cookie's
// `groups` param (e.g. "C0001:1,C0002:0,C0004:0"). Used for the initial read at
// page-load time, where the cookie reliably reflects the persisted choice.
// On a *change*, neither this cookie nor window.OnetrustActiveGroups is refreshed
// synchronously when the user toggles a category off and saves — a just-disabled
// group still appears active. Consent changes are therefore handled via the
// OnConsentChanged event (see initOneTrust), whose detail is the fresh value.
function readGroupStates(): Record<string, boolean> {
  const cookie = document.cookie
    .split('; ')
    .find(c => c.startsWith('OptanonConsent='));
  if (!cookie) return {};
  const params = new URLSearchParams(decodeURIComponent(cookie.split('=').slice(1).join('=')));
  const groups = params.get('groups') ?? '';
  const states: Record<string, boolean> = {};
  for (const pair of groups.split(',')) {
    const [id, on] = pair.split(':');
    if (id) states[id.trim()] = on?.trim() === '1';
  }
  return states;
}

function parseActiveGroups(): { analytics: boolean; marketing: boolean } {
  const states = readGroupStates();
  // Fall back to the (page-load-only reliable) global if the cookie is absent.
  if (Object.keys(states).length === 0) {
    const groups = (window.OnetrustActiveGroups ?? '').split(',').map(g => g.trim());
    return { analytics: groups.includes('C0002'), marketing: groups.includes('C0004') };
  }
  return {
    analytics: states['C0002'] === true,
    marketing: states['C0004'] === true,
  };
}

export function initOneTrust(domainScriptId: string): void {
  let listenerRegistered = false;

  // OneTrust requires OptanonWrapper to be a DOM script declaration, not just a
  // window property. We bridge via a window callback so module code stays reachable.
  // OneTrust calls OptanonWrapper once it is fully initialised (on page load), and
  // again after the banner/preference centre closes.
  window.__proximaConsentCallback = () => {
    // Register the OnConsentChanged listener once OneTrust is available. This is
    // the authoritative source for consent updates: event.detail carries the
    // freshly-updated active group IDs, so we never depend on the cookie or the
    // OnetrustActiveGroups global — neither of which is refreshed synchronously
    // when the user toggles a category off and saves.
    if (!listenerRegistered && typeof window.OneTrust?.OnConsentChanged === 'function') {
      listenerRegistered = true;
      window.OneTrust.OnConsentChanged((event) => {
        const detail = (event as CustomEvent<string[] | string>).detail;
        const activeGroupIds = Array.isArray(detail)
          ? detail
          : String(detail ?? '').split(',').map(g => g.trim()).filter(Boolean);
        saveConsent(mapActiveGroups(activeGroupIds));
      });
    }
    // Initial read on load: the cookie is authoritative at page-load time.
    saveConsent(parseActiveGroups());
  };

  const sdkScript = document.createElement('script');
  sdkScript.type = 'text/javascript';
  sdkScript.setAttribute('charset', 'UTF-8');
  sdkScript.setAttribute('data-domain-script', domainScriptId);
  sdkScript.src = 'https://cdn-ukwest.onetrust.com/scripttemplates/otSDKStub.js';
  document.head.appendChild(sdkScript);

  const wrapperScript = document.createElement('script');
  wrapperScript.type = 'text/javascript';
  wrapperScript.textContent = 'function OptanonWrapper(){window.__proximaConsentCallback();}';
  document.head.appendChild(wrapperScript);
}

// Opens the OneTrust preference centre so the user can change their consent.
// No-op when OneTrust is not configured/loaded for this space.
export function openPreferences(): void {
  window.OneTrust?.ToggleInfoDisplay?.();
}
